import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, HeartHandshake, Sparkles } from 'lucide-react';
import AppLogo from '../components/AppLogo';
import CareProfileResults from '../components/assessments/CareProfileResults';
import { ScaleQuestion, SingleQuestion, TextQuestion } from '../components/assessments/QuestionInputs';

const AID = 'care_profile';

export default function CareProfilePage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [responses, setResponses] = useState({});
  const [stage, setStage] = useState('loading'); // loading | results | intro | section | saving
  const [sectionIdx, setSectionIdx] = useState(0);
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cfgRes, progRes] = await Promise.all([
          axios.get(`${API}/assessments/${AID}/config`),
          axios.get(`${API}/assessments/${AID}/progress`),
        ]);
        setConfig(cfgRes.data);
        if (progRes.data.has_results) {
          const r = await axios.get(`${API}/assessments/${AID}/results`);
          setResults(r.data);
          setStage('results');
        } else if (progRes.data.progress) {
          setResponses(progRes.data.progress.responses || {});
          const done = progRes.data.progress.completed_sections || [];
          const firstIncomplete = cfgRes.data.sections.findIndex(s => !done.includes(s.id));
          setSectionIdx(firstIncomplete === -1 ? cfgRes.data.sections.length - 1 : firstIncomplete);
          setStage(done.length > 0 ? 'section' : 'intro');
        } else {
          setStage('intro');
        }
      } catch (e) { toast.error('Could not load the assessment'); setStage('intro'); }
    })();
  }, []);

  const setAnswer = (qid, value) => setResponses(prev => ({ ...prev, [qid]: value }));

  const visibleQuestions = (section) => section.questions.filter(q =>
    !q.show_if || responses[q.show_if.question] === q.show_if.equals
  );

  const sectionComplete = (section) => visibleQuestions(section)
    .filter(q => !q.optional)
    .every(q => responses[q.id] !== undefined && responses[q.id] !== '');

  const saveSection = async (section) => {
    const sectionResponses = {};
    section.questions.forEach(q => { if (responses[q.id] !== undefined) sectionResponses[q.id] = responses[q.id]; });
    await axios.put(`${API}/assessments/${AID}/progress`, {
      responses: sectionResponses,
      completed_sections: [section.id],
    });
  };

  const handleNext = async () => {
    const section = config.sections[sectionIdx];
    if (!sectionComplete(section)) { toast.error('Please answer every question in this section'); return; }
    setSaving(true);
    try {
      await saveSection(section);
      if (sectionIdx < config.sections.length - 1) {
        setSectionIdx(sectionIdx + 1);
        window.scrollTo({ top: 0 });
      } else {
        const res = await axios.post(`${API}/assessments/${AID}/complete`);
        setResults(res.data);
        setStage('results');
        toast.success('Your Care Profile is ready!');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save — please try again'); }
    setSaving(false);
  };

  const handleRetake = () => {
    setResponses({});
    setResults(null);
    setSectionIdx(0);
    setStage('intro');
  };

  if (stage === 'loading' || !config) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-600" /></div>;
  }

  if (stage === 'results' && results) {
    return <CareProfileResults results={results} onRetake={handleRetake} />;
  }

  if (stage === 'intro') {
    return (
      <div className="min-h-screen pb-24 bg-background" data-testid="care-profile-intro">
        <div className="max-w-lg mx-auto px-6 pt-12 space-y-6 text-center">
          <div className="flex justify-center"><AppLogo /></div>
          <HeartHandshake className="w-12 h-12 text-cyan-600 mx-auto" />
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{config.intro.heading}</h1>
          <p className="text-sm text-muted-foreground leading-relaxed text-left">{config.intro.body}</p>
          <p className="text-sm text-cyan-700 dark:text-cyan-300 leading-relaxed">{config.intro.footer}</p>
          <p className="text-xs text-muted-foreground">{config.estimated_minutes} minutes · progress saves after every section</p>
          <Button className="btn-primary w-full" onClick={() => setStage('section')} data-testid="start-assessment-btn">
            Start the Assessment <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <button className="text-xs text-muted-foreground underline" onClick={() => navigate(-1)}>Maybe later</button>
        </div>
      </div>
    );
  }

  const section = config.sections[sectionIdx];
  const pct = Math.round((sectionIdx / config.sections.length) * 100);

  return (
    <div className="min-h-screen pb-32 bg-background" data-testid="care-profile-section">
      <div className="surface-header sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-muted-foreground">Section {sectionIdx + 1} of {config.sections.length}</p>
            <p className="text-xs font-semibold text-cyan-600">{pct}%</p>
          </div>
          <Progress value={pct} className="h-1.5" />
        </div>
      </div>
      <div className="max-w-lg mx-auto px-4 pt-6 space-y-4">
        <div>
          <h2 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="section-title">{section.title}</h2>
          <p className="text-sm text-muted-foreground">{section.description}</p>
        </div>
        {visibleQuestions(section).map(q => {
          if (q.type === 'scale') return <ScaleQuestion key={q.id} q={q} value={responses[q.id]} onChange={setAnswer} />;
          if (q.type === 'single') return <SingleQuestion key={q.id} q={q} value={responses[q.id]} onChange={setAnswer} />;
          return <TextQuestion key={q.id} q={q} value={responses[q.id]} onChange={setAnswer} />;
        })}
        <div className="flex gap-3 pt-2">
          {sectionIdx > 0 && (
            <Button variant="outline" onClick={() => { setSectionIdx(sectionIdx - 1); window.scrollTo({ top: 0 }); }} data-testid="prev-section-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          <Button className="btn-primary flex-1" onClick={handleNext} disabled={saving} data-testid="next-section-btn">
            {saving ? 'Saving...' : sectionIdx === config.sections.length - 1
              ? <span className="flex items-center">See My Care Profile <Sparkles className="w-4 h-4 ml-2" /></span>
              : <span className="flex items-center">Next <ArrowRight className="w-4 h-4 ml-2" /></span>}
          </Button>
        </div>
      </div>
    </div>
  );
}
